import React, { useEffect, useState } from "react";
import cn from "classnames";
import { ThemeProvider, useTheme } from "styled-components";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { IPitch, ISolfaNameType } from "../../../../../../common/types";
import { IStateTree } from "../../../../../store/interfaces";
import { IColorPalette, IUser } from "../../../../../store/user/models";
import { getColors, solfaNamesArray, isPitchPulsating, lengthenSolfa } from "../../../../../../common/parameters";
import Draggable from "../../../../Draggable";

import getBirdsPath from "../../Common/getBirdsPath";

import {
    StyledSolfaLadder,
    Container,
    SolfaButtons,
    SolfaNameButton,
    SolfaButtonIn,
    SmallCircle,
    Line,
    AssetWrapper,
} from "./styles";
import {
    fillSolfaNames,
    isPitchSelected,
    getSolfaFlat,
} from "./utils";
import { IProps } from "./interfaces";

const SolfaLadder = ({
    solfaNameTypes,
    handleInput,
    frameScale,
    isLow,
    currentNote,
    hint,
    isEditor,
    selectedPitch,
    withColors,
}: IProps) => {
    const [solfaNames, setSolfaNames] = useState<ISolfaNameType[]>([]);
    const [dragPositions, setDragPositions] = useState<{ x: number, y: number }[]>([]);
    const [transition, setTransition] = useState(false);
    const [highlighted, setHighlighted] = useState<null | number>(null);
    const [middleButton, setMiddleButton] = useState<null | number>(null);

    const { colors = 1, solfaShort = false, solfaKam = false } = useSelector((state: IStateTree) => state.user as IUser);
    const theme = useTheme();
    const { locale } = useIntl();

    useEffect(() => {
        let solfaNames = fillSolfaNames(solfaNameTypes);
        let middleButton = Math.round(solfaNameTypes.length * 0.5);
        if (solfaNames.length === 1) middleButton = 0;
        if (!solfaNames[middleButton].show) {
            middleButton--;
        }
        if (!solfaNames[middleButton].show) {
            middleButton++;
            middleButton++;
        }
        setMiddleButton(middleButton);
        setSolfaNames(solfaNames);
    }, [solfaNameTypes])

    const resetDrags = () => {
        setDragPositions([]);
        setTransition(true);
        setTimeout(() => {
            setTransition(false);
        }, 400);
    };

    const lengthenSolfaHelper = (solfaSolfa: string, x: number, solfaShort: boolean) => {
        if ((x > 24 || x < -24) && locale !== "lt") {
            if (locale === "hu" && !solfaShort) {
                if (solfaSolfa.slice(0, 1) === "s") {
                    return solfaSolfa.replace("s", "sz");
                }
            }
            return solfaSolfa;
        }
        return lengthenSolfa(solfaSolfa, locale, solfaShort)
    }

    return (
        <ThemeProvider theme={{ ...theme, c: { ...theme.c, solfa: getColors(colors as IColorPalette | number) } }}>
            <StyledSolfaLadder>
                <Container isLow={isLow} isEditor={!!isEditor}>
                    <SolfaButtons>
                        <Line />
                        {solfaNames.map((solfaName, index) => {
                            if (solfaName.i < 0) return null;
                            return (
                                <SolfaNameButton
                                    key={solfaName.i}
                                    coloring={!!withColors}
                                    className={cn(
                                        !solfaName.show && "smallSeed",
                                        highlighted === solfaName.i && "highlight"
                                    )}
                                    marginRight={solfaName.i % 7 !== 2 && solfaName.i % 7 !== 6}
                                >
                                    {solfaName.show && (
                                        <Draggable
                                            transition={transition}
                                            noBoundry
                                            onStart={() => {
                                                setHighlighted(solfaName.i);
                                            }}
                                            onDrag={(x: number, y: number) => {
                                                let min = -28;
                                                let max = 28;
                                                if (solfaName.i % 7 === 2 || solfaName.i % 7 === 6) {
                                                    max = 0;
                                                }
                                                if (solfaName.i % 7 === 3 || solfaName.i % 7 === 0) {
                                                    min = 0;
                                                }
                                                if (dragPositions[solfaName.i]) {
                                                    setDragPositions(dragPositions.map((dragPosition, i) => {
                                                        if (i === solfaName.i) {
                                                            return {
                                                                x: Math.min(max, Math.max(min, x)),
                                                                y: 0,
                                                            };
                                                        }
                                                        return dragPosition;
                                                    }))
                                                } else {
                                                    const newDragPositions = [];
                                                    newDragPositions[solfaName.i] = {
                                                        x: Math.min(max, Math.max(min, x)),
                                                        y: 0,
                                                    }
                                                    setDragPositions(newDragPositions);
                                                }
                                            }}
                                            position={{
                                                x: dragPositions[solfaName.i] ? dragPositions[solfaName.i].x : 0,
                                                y: dragPositions[solfaName.i] ? dragPositions[solfaName.i].y : 0,
                                            }}
                                            scale={frameScale}
                                            onClick={(e: MouseEvent | TouchEvent) => {
                                                if (solfaName.show) {
                                                    handleInput(solfaName.s!, solfaName.o || 0, undefined, e);
                                                }
                                                setHighlighted(null);
                                            }}
                                            onStop={(e: MouseEvent | TouchEvent) => {
                                                if (dragPositions[solfaName.i] && dragPositions[solfaName.i].x > 24) {
                                                    handleInput(solfaName.s!, solfaName.o || 0, 1, e);
                                                } else if (
                                                    dragPositions[solfaName.i] &&
                                                    dragPositions[solfaName.i].x < -24
                                                ) {
                                                    handleInput(solfaName.s!, solfaName.o || 0, -1, e);
                                                } else {
                                                    handleInput(solfaName.s!, solfaName.o || 0, 0, e);
                                                }
                                                resetDrags();
                                                setHighlighted(null);
                                            }}
                                        >
                                            <SolfaButtonIn
                                                className={cn({
                                                    pulsate:
                                                        currentNote &&
                                                        hint &&
                                                        isPitchPulsating(solfaName, currentNote.p as IPitch),
                                                    Selected: isPitchSelected(selectedPitch, solfaName),
                                                })}
                                                solfaShort={solfaNamesArray("hu")[solfaName.i].slice(0, 1)}
                                                coloring={!!withColors}
                                                modified={
                                                    dragPositions[solfaName.i] &&
                                                    (dragPositions[solfaName.i].x > 24 ||
                                                        dragPositions[solfaName.i].x < -24)
                                                }
                                            >
                                                {lengthenSolfaHelper(solfaNamesArray(locale)[solfaName.i].slice(0, 1), dragPositions[solfaName.i]?.x || 0, solfaShort)}
                                                {dragPositions[solfaName.i] && dragPositions[solfaName.i].x > 24 && (
                                                    <span>{locale === "lt" ? "#" : "i"}</span>
                                                )}
                                                {dragPositions[solfaName.i] && dragPositions[solfaName.i].x < -24 && (
                                                    <span className={locale === "lt" ? "italic" : undefined}>{locale === "lt" ? "b" : getSolfaFlat(solfaName, locale, solfaKam)}</span>
                                                )}
                                                {solfaNamesArray(locale)[solfaName.i].slice(1)}
                                                {middleButton === index && isLow && (
                                                    <AssetWrapper>
                                                        <img
                                                            src={getBirdsPath("/solfaName/asset-04.svg")}
                                                            alt="decoration"
                                                        />
                                                    </AssetWrapper>
                                                )}
                                            </SolfaButtonIn>
                                        </Draggable>
                                    )}
                                    {!solfaName.show && <SmallCircle />}
                                </SolfaNameButton>
                            )
                        })}
                    </SolfaButtons>
                </Container>
            </StyledSolfaLadder>
        </ThemeProvider>
    );
}


export default SolfaLadder;
